/**
 * Cross-cutting payment data-access layer — shared by both the credit-card and bank-account
 * domains. Domain-specific data access lives in `creditCard/creditCardApi.ts`,
 * `bankAccount/bankAccountApi.ts`, and `memberInfo/memberInfoApi.ts`.
 */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import {
  MySavedPaymentMethodsDocument,
  type MySavedPaymentMethodsFailureCode,
  SetDefaultPaymentMethodDocument,
  type SetDefaultPaymentMethodFailureCode,
} from '@/graphql/generated/graphql';

import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import type { BillingAddressValues } from '@/lib/payments/paymentAccount.types';
import { mapSavedBankAccount } from './bankAccount/bankAccountApi';
import { mapSavedCreditCard } from './creditCard/creditCardApi';
import type { UpcomingPayment } from './upcomingPayments/upcomingPaymentsApi';

// The backend's BillingAddressInput fields are optional but reject empty strings (min length 1) —
// omit rather than send `''` for a field the member left blank (e.g. line2).
export function toBillingAddressInput(billing: BillingAddressValues) {
  return {
    line1: billing.line1 || undefined,
    line2: billing.line2 || undefined,
    city: billing.city || undefined,
    state: billing.state || undefined,
    zip: billing.zip || undefined,
  };
}

/**
 * Sets any active, owned payment method (credit card or bank account) as the caller's default.
 * Type-agnostic and minimal by design — Salesforce demotes the prior default server-side, and the
 * response is just the target row's id/isDefault, not a full card (see useSetDefaultPaymentMethod
 * for how the caller reconciles the rest of the list).
 */
export async function requestSetDefaultPaymentMethod(id: string): Promise<{ id: string; isDefault: boolean }> {
  const data = await gqlRequest(SetDefaultPaymentMethodDocument, { id });
  const result = data.setDefaultPaymentMethod;
  if (result.__typename === 'SetDefaultPaymentMethodFailure') {
    throw new PaymentSetDefaultError(result.code);
  }
  return { id: result.id, isDefault: result.isDefault };
}

/**
 * Thrown when setDefaultPaymentMethod returns its Failure variant. `NOT_FOUND` is the only code,
 * deliberately overloading unknown id / not-owned-by-caller / deactivated to avoid an enumeration
 * oracle — same reasoning as PaymentEditCardError.
 */
export class PaymentSetDefaultError extends Error {
  constructor(public readonly code: SetDefaultPaymentMethodFailureCode) {
    super(code);
    this.name = 'PaymentSetDefaultError';
  }
}

/** Thrown when mySavedPaymentMethods returns a domain Failure variant (currently only INVALID_INPUT). */
export class SavedPaymentMethodsError extends Error {
  constructor(public readonly code: MySavedPaymentMethodsFailureCode) {
    super(code);
    this.name = 'SavedPaymentMethodsError';
  }
}

/** Raw cache shape behind both {@link useSavedCreditCards} and {@link useSavedBankAccounts}. */
export interface SavedPaymentMethodsData {
  cards: CreditCardAccount[];
  bankAccounts: BankAccountAccount[];
}

/**
 * Shared by `useSavedCreditCards`/`useSavedBankAccounts` (same queryKey, different `select`) so
 * mounting both against one `mySavedPaymentMethods` query doesn't fire it twice, and by every
 * mutation hook below that needs to read/write the cache directly (optimistic updates, not just
 * `select`, since `select` only transforms what `useQuery` returns to a consumer).
 */
export const savedPaymentMethodsKey = ['payments', 'savedPaymentMethods'] as const;

export async function requestSavedPaymentMethods(): Promise<SavedPaymentMethodsData> {
  const data = await gqlRequest(MySavedPaymentMethodsDocument, { input: {} });
  const result = data.mySavedPaymentMethods;
  if (result.__typename === 'MySavedPaymentMethodsFailure') {
    throw new SavedPaymentMethodsError(result.code);
  }
  return {
    cards: result.edges.filter((edge) => edge.__typename === 'SavedCreditCard').map(mapSavedCreditCard),
    bankAccounts: result.edges.filter((edge) => edge.__typename === 'SavedBankAccount').map(mapSavedBankAccount),
  };
}

export function selectCreditCards(data: SavedPaymentMethodsData): CreditCardAccount[] {
  return data.cards;
}

export function selectBankAccounts(data: SavedPaymentMethodsData): BankAccountAccount[] {
  return data.bankAccounts;
}

/**
 * A saved account of either type, flattened to just what an account picker needs. Lets the one-time
 * payment modal offer cards and bank accounts in one list without knowing either row type, and
 * keeps label formatting in the component where the translations live.
 */
export interface SavedPaymentAccount {
  id: string;
  kind: 'card' | 'bank';
  isDefault: boolean;
  /** Always false for bank accounts, which carry no expiration. */
  isExpired: boolean;
  /** Card network code (e.g. "VISA"), or the bank's name. */
  descriptor: string;
  /** Masked tail: a card's last 4 or a bank account's last 5-6. */
  suffix: string;
  expirationMonth: number | null;
  expirationYear: number | null;
}

/**
 * Cards followed by bank accounts, in cache order. Callers pick the default themselves, matching
 * how the AutoPay picker resolves its selection.
 *
 * Deactivated profiles are dropped: Salesforce keeps the row after a removal, and charging one
 * fails server-side as `NOT_FOUND`, so offering it would only produce a late, opaque error. Legacy
 * achieved the same by querying with `filters: { active: true }`. Scoped to this selector rather
 * than the shared request, since the Payment Accounts page's own filtering is a separate question.
 */
export function selectAllPaymentAccounts(data: SavedPaymentMethodsData): SavedPaymentAccount[] {
  return [
    ...data.cards
      .filter((card) => card.isActive)
      .map((card) => ({
        id: card.id,
        kind: 'card' as const,
        isDefault: card.isDefault,
        isExpired: card.isExpired,
        descriptor: card.network,
        suffix: card.last4,
        expirationMonth: card.expirationMonth,
        expirationYear: card.expirationYear,
      })),
    ...data.bankAccounts
      .filter((account) => account.isActive)
      .map((account) => ({
        id: account.id,
        kind: 'bank' as const,
        isDefault: account.isDefault,
        isExpired: false,
        descriptor: account.bankName,
        suffix: account.last6,
        expirationMonth: null,
        expirationYear: null,
      })),
  ];
}

/**
 * The member's default saved method, if any — Salesforce's single-default invariant spans both
 * types, so at most one row can match. Built on {@link selectAllPaymentAccounts} to inherit its
 * deactivated-row filter: a removed profile keeps its `isDefault` flag in Salesforce, and
 * presenting one as the member's default would name an account that can no longer be charged.
 */
export function selectDefaultPaymentMethod(data: SavedPaymentMethodsData): SavedPaymentAccount | undefined {
  return selectAllPaymentAccounts(data).find((account) => account.isDefault);
}

/**
 * The saved-method ids (`Payment_Profile__c`) with at least one scheduled one-time payment —
 * shared by the credit-card and bank-account "Remove" guards, since both hide the action for the
 * same reason. `paymentProfileId` is nullable and, per its own field description, "not guaranteed
 * to have a matching row in mySavedPaymentMethods" — rows without it are simply excluded rather
 * than treated as an error.
 */
export function selectPaymentProfileIdsWithUpcomingPayment(payments: UpcomingPayment[]): Set<string> {
  return new Set(payments.flatMap((payment) => (payment.paymentProfileId ? [payment.paymentProfileId] : [])));
}

/**
 * Demotes every other saved payment method's default flag and promotes `id`. Salesforce's
 * single-default invariant spans both credit cards and bank accounts, so promoting one must demote
 * whichever row of either type currently holds the flag — not just other rows of the same type.
 */
export function promoteDefault(data: SavedPaymentMethodsData, id: string): SavedPaymentMethodsData {
  return {
    cards: data.cards.map((card) => ({ ...card, isDefault: card.id === id })),
    bankAccounts: data.bankAccounts.map((account) => ({ ...account, isDefault: account.id === id })),
  };
}
